import type { AxiosInstance } from "axios";

/**
 * Typed calls onto the admin API (apps/api modules/admin). These mirror the server's
 * response shapes rather than importing them — a browser bundle cannot pull in server
 * code, the same trade the merchant app's libs make.
 */

export interface AdminUser {
  readonly id: string;
  readonly email: string;
  readonly name: string;
}

export type SubscriptionStatus = "trialing" | "active" | "past_due" | "paused" | "canceled";

export interface DirectoryStore {
  readonly id: string;
  readonly shopDomain: string;
  readonly displayName: string;
  readonly status: string;
  readonly onboarding: string;
  readonly installedAt: string | null;
}

export interface DirectoryClient {
  readonly id: string;
  readonly name: string;
  readonly stores: readonly DirectoryStore[];
}

export interface DirectorySubscription {
  readonly planCode: string;
  readonly planName: string;
  readonly status: SubscriptionStatus;
  /** "manual" | "razorpay" | "shopify" — "manual" is the comp/no-billing case. */
  readonly provider: string;
  readonly trialEndsAt: string | null;
  readonly limits: unknown;
}

export interface DirectoryOrg {
  readonly id: string;
  readonly name: string;
  readonly kind: string;
  /** "active" | "paused" — paused suspends the whole org's data flow. */
  readonly status: string;
  readonly billingEmail: string;
  readonly createdAt: string;
  readonly clients: readonly DirectoryClient[];
  readonly storeCount: number;
  readonly memberCount: number;
  readonly subscription: DirectorySubscription | null;
}

export interface EntitlementInspection {
  readonly orgId: string;
  readonly orgName: string;
  readonly planCode: string | null;
  readonly status: string | null;
  readonly trialEndsAt: string | null;
  readonly trialExpired: boolean;
  readonly reports: readonly { readonly key: string; readonly included: boolean }[];
}

export interface AdminMember {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly role: string;
  readonly clientId: string | null;
  readonly clientName: string | null;
  readonly status: "invited" | "active";
}

export interface PlanSummary {
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  readonly visibility: string;
  readonly priceMinor: number;
  readonly currency: string;
  readonly interval: string;
  readonly trialDays: number;
  readonly isActive: boolean;
  readonly limits: unknown;
  readonly reportKeys: readonly string[];
  readonly subscriberCount: number;
}

export type Rag = "green" | "amber" | "red";

export interface AdminHealthAdSync {
  readonly platform: string;
  readonly connected: boolean;
  readonly status: Rag;
  readonly lastSuccessAt: string | null;
  readonly consecutiveFailures: number;
}

export interface AdminHealthStore {
  readonly tenantId: string;
  readonly shopDomain: string;
  readonly displayName: string;
  readonly status: Rag;
  readonly lastEventAt: string | null;
  readonly events48h: number;
  readonly orders48h: number;
  readonly ordersTracked48h: number;
  readonly v2Share: number | null;
  readonly findings: readonly { readonly severity: string; readonly code: string; readonly message: string }[];
  readonly adSyncs: readonly AdminHealthAdSync[];
}

export interface AdminHealthBoard {
  readonly generatedAt: string;
  readonly counts: { readonly green: number; readonly amber: number; readonly red: number };
  readonly dataFreshness: { readonly status: Rag; readonly minutesBehind: number | null };
  readonly stores: readonly AdminHealthStore[];
}

export interface AuditRow {
  readonly id: string;
  readonly orgId: string | null;
  readonly actorType: string;
  readonly actorUserId: string | null;
  readonly actorEmail: string | null;
  readonly action: string;
  readonly resourceType: string;
  readonly resourceId: string;
  readonly before: unknown;
  readonly after: unknown;
  readonly ip: string | null;
  readonly createdAt: string;
}

function data<T>(response: { data: { data: T } }): T {
  return response.data.data;
}

export interface AdminOverview {
  readonly orgs: number;
  readonly members: number;
  readonly stores: { readonly active: number; readonly paused: number; readonly uninstalled: number };
  readonly mrrMinor: number;
  readonly currency: string;
  readonly orgsByPlan: readonly { readonly code: string; readonly name: string; readonly subscribers: number }[];
  readonly newStoresByWeek: readonly { readonly weekStart: string; readonly count: number }[];
  readonly onboarding: readonly { readonly state: string; readonly count: number }[];
  readonly ordersByDay: readonly { readonly day: string; readonly orders: number; readonly gmv: number }[];
  readonly eventsByDay: readonly { readonly day: string; readonly events: number }[];
}

export async function fetchOverview(client: AxiosInstance): Promise<AdminOverview> {
  return data(await client.get<{ data: AdminOverview }>("/admin/overview"));
}

export async function fetchAdminMe(client: AxiosInstance): Promise<AdminUser> {
  return data(await client.get<{ data: AdminUser }>("/admin/me"));
}

export async function adminLogin(client: AxiosInstance, email: string, password: string): Promise<AdminUser> {
  return data(await client.post<{ data: AdminUser }>("/admin/auth/login", { email, password }));
}

export async function adminLogout(client: AxiosInstance): Promise<void> {
  await client.post("/admin/auth/logout").catch(() => undefined);
}

export async function fetchDirectory(client: AxiosInstance, search?: string): Promise<readonly DirectoryOrg[]> {
  return data(
    await client.get<{ data: readonly DirectoryOrg[] }>("/admin/orgs", {
      params: search !== undefined && search !== "" ? { search } : {},
    }),
  );
}

export async function fetchEntitlements(client: AxiosInstance, orgId: string): Promise<EntitlementInspection> {
  return data(await client.get<{ data: EntitlementInspection }>(`/admin/orgs/${orgId}/entitlements`));
}

export async function fetchOrgMembers(client: AxiosInstance, orgId: string): Promise<readonly AdminMember[]> {
  return data(await client.get<{ data: readonly AdminMember[] }>(`/admin/orgs/${orgId}/members`));
}

export async function assignPlan(
  client: AxiosInstance,
  orgId: string,
  planCode: string,
  status: SubscriptionStatus,
): Promise<EntitlementInspection> {
  return data(
    await client.post<{ data: EntitlementInspection }>(`/admin/orgs/${orgId}/subscription`, { planCode, status }),
  );
}

export async function setOrgStatus(client: AxiosInstance, orgId: string, status: "active" | "paused"): Promise<{ orgId: string; status: string }> {
  return data(await client.post<{ data: { orgId: string; status: string } }>(`/admin/orgs/${orgId}/status`, { status }));
}

export async function setStoreStatus(client: AxiosInstance, storeId: string, status: "active" | "paused"): Promise<{ storeId: string; status: string }> {
  return data(await client.post<{ data: { storeId: string; status: string } }>(`/admin/stores/${storeId}/status`, { status }));
}

export async function fetchPlans(client: AxiosInstance): Promise<readonly PlanSummary[]> {
  return data(await client.get<{ data: readonly PlanSummary[] }>("/admin/plans"));
}

export async function fetchHealth(client: AxiosInstance): Promise<AdminHealthBoard> {
  return data(await client.get<{ data: AdminHealthBoard }>("/admin/health"));
}

export async function setPlanReports(
  client: AxiosInstance,
  planCode: string,
  reportKeys: readonly string[],
): Promise<PlanSummary> {
  return data(await client.post<{ data: PlanSummary }>(`/admin/plans/${planCode}/reports`, { reportKeys }));
}

export interface CreatePlanInput {
  readonly code: string;
  readonly name: string;
  readonly kind: "brand" | "agency";
  readonly visibility?: "public" | "custom";
  readonly priceMinor: number;
  readonly currency?: string;
  readonly interval: "monthly" | "yearly";
  readonly trialDays: number;
  readonly limits: Record<string, number | null>;
  readonly reportKeys: readonly string[];
}

export interface UpdatePlanInput {
  readonly name?: string;
  readonly priceMinor?: number;
  readonly interval?: "monthly" | "yearly";
  readonly trialDays?: number;
  readonly isActive?: boolean;
  readonly limits?: Record<string, number | null>;
}

export async function createPlan(client: AxiosInstance, input: CreatePlanInput): Promise<PlanSummary> {
  return data(await client.post<{ data: PlanSummary }>("/admin/plans", input));
}

export async function updatePlan(client: AxiosInstance, code: string, patch: UpdatePlanInput): Promise<PlanSummary> {
  return data(await client.patch<{ data: PlanSummary }>(`/admin/plans/${code}`, patch));
}

/** Read a numeric limit from a plan's `limits` json; "" means unlimited/unset. */
export function planLimit(limits: unknown, key: string): string {
  if (typeof limits === "object" && limits !== null) {
    const value = (limits as Record<string, unknown>)[key];
    if (typeof value === "number") return String(value);
  }
  return "";
}

export async function publishPlan(client: AxiosInstance, planCode: string): Promise<unknown> {
  return data(await client.post<{ data: unknown }>(`/admin/plans/${planCode}/publish`, {}));
}

export interface StaffMember {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly disabledAt: string | null;
  readonly createdAt: string;
}

export async function fetchStaff(client: AxiosInstance): Promise<readonly StaffMember[]> {
  return data(await client.get<{ data: readonly StaffMember[] }>("/admin/staff"));
}

export async function createStaff(
  client: AxiosInstance,
  email: string,
  name: string,
): Promise<StaffMember & { temporaryPassword: string }> {
  return data(await client.post<{ data: StaffMember & { temporaryPassword: string } }>("/admin/staff", { email, name }));
}

export async function setStaffDisabled(client: AxiosInstance, staffId: string, disabled: boolean): Promise<StaffMember> {
  return data(await client.post<{ data: StaffMember }>(`/admin/staff/${staffId}/status`, { disabled }));
}

export async function fetchAudit(
  client: AxiosInstance,
  query: { orgId?: string; action?: string; resourceId?: string } = {},
): Promise<readonly AuditRow[]> {
  const params: Record<string, string> = {};
  if (query.orgId !== undefined && query.orgId !== "") params["orgId"] = query.orgId;
  if (query.action !== undefined && query.action !== "") params["action"] = query.action;
  if (query.resourceId !== undefined && query.resourceId !== "") params["resourceId"] = query.resourceId;
  return data(await client.get<{ data: readonly AuditRow[] }>("/admin/audit", { params }));
}

/** A subscription that bills nothing: settled outside any provider, full access. */
export function isComped(sub: Pick<DirectorySubscription, "provider" | "status"> | null): boolean {
  return sub !== null && sub.provider === "manual" && (sub.status === "active" || sub.status === "trialing");
}

/**
 * The report catalogue, duplicated from @ecomally/contracts REPORT_CATALOG on purpose.
 *
 * The contracts package only exports its barrel, and that barrel pulls in node:crypto
 * (identity-hash) which cannot be bundled for a browser — the same reason apps/web keeps
 * its own copy in lib/reports.ts. These five keys are stable; renaming one breaks plans,
 * so it is not a list that churns.
 */
export const REPORT_CATALOG: readonly { readonly key: string; readonly name: string }[] = [
  { key: "landing-pages", name: "Landing Page Analysis" },
  { key: "landing-page-trend", name: "Landing Page Analysis (DOW)" },
  { key: "source-attribution", name: "Source Attribution" },
  { key: "meta-campaign-attribution", name: "Meta Campaign Attribution" },
  { key: "google-campaign-attribution", name: "Google Campaign Attribution" },
];
